import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import {
    Typography,
    Grid,
    Chip,
    Button,
    Box,
    CircularProgress,
    Stack,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton
} from '@mui/material';
import {
    CalendarMonth as CalendarIcon,
    LocationOn as LocationIcon,
    Code as CodeIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    Close as CloseIcon,
    Article as ArticleIcon,
    FileDownload as FileDownloadIcon,
    Visibility as VisibilityIcon,
    InsertDriveFile as InsertDriveFileIcon,
    Image as ImageIcon,
    TableChart as TableChartIcon,
    DescriptionSharp
} from '@mui/icons-material';

import MainCard from 'ui-component/cards/MainCard';
import { useTravelRequest } from 'hooks/api/travel-request/useTravelRequest';
import { theme } from '../ConstantColors';
import { formatFileSize } from 'utils/fileUtils';
import TableCollapsible from './daily-exp/DailyExpensesCollapsibleTable';
import { BASE_URL } from 'utils/axios';
import { configuration } from 'utils/environment/configuration';

const TripDetails = () => {
    const { requestId } = useParams();
    const { getRequestById, getLineActivities, selectedRequest, lineActivities, loading } = useTravelRequest();
    const [error, setError] = useState(null);
    const { keycloak } = useKeycloak();
    const [documents, setDocuments] = useState([]);
    const [currentLoadingDoc, setCurrentLoadingDoc] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);

    useEffect(() => {
        const fetchTripDetails = async () => {
            try {
                await getRequestById(requestId);
                await getLineActivities(requestId);
            } catch (err) {
                setError('Failed to load trip details');
                console.error(err);
            }
        };

        fetchTripDetails();
    }, [requestId, getRequestById, getLineActivities]);

    useEffect(() => {
        const fetchTravelPlanDocuments = async () => {
            if (!selectedRequest?.travelPlan?.id || !keycloak?.token) return;

            try {
                setIsLoading(true);
                const response = await fetch(`${BASE_URL}/api/travel-plans/${selectedRequest.travelPlan.id}/documents`, {
                    headers: {
                        Accept: 'application/json',
                        Authorization: `Bearer ${keycloak.token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Request failed: ${response.status}`);
                }

                const data = await response.json();
                setDocuments(data.content || []);
            } catch (error) {
                console.error('Error fetching travel plan documents:', error);
                setDocuments([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTravelPlanDocuments();
    }, [selectedRequest?.travelPlan?.id, keycloak?.token]);
    const formatCurrency = (amount) => `$${Number(amount).toFixed(2)}`;
    const formatDate = (dateString) => new Date(dateString).toLocaleDateString();
    const getFileDetails = (fileName) => {
        const extension = fileName?.toLowerCase().split('.').pop();
        let icon = <ArticleIcon />;
        let canPreview = false;

        switch (extension) {
            case 'pdf':
                icon = <ArticleIcon />;
                canPreview = true;
                break;
            case 'png':
            case 'jpg':
            case 'jpeg':
            case 'gif':
                icon = <ImageIcon />;
                canPreview = true;
                break;
            case 'doc':
            case 'docx':
                icon = <DescriptionSharp />;
                canPreview = false;
                break;
            case 'xls':
            case 'xlsx':
                icon = <TableChartIcon />;
                canPreview = false;
                break;
            default:
                icon = <InsertDriveFileIcon />;
                canPreview = false;
        }

        return { icon, canPreview, extension };
    };

    const handleDocumentDownload = async (documentId, fileName) => {
        try {
            setCurrentLoadingDoc(documentId);
            setIsLoading(true);

            const response = await fetch(`${configuration.apiUrl}/api/documents/${documentId}`, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
        } finally {
            setIsLoading(false);
            setCurrentLoadingDoc(null);
        }
    };

    const handleDocumentPreview = async (documentId, fileName) => {
        const { canPreview } = getFileDetails(fileName);

        if (!canPreview) {
            handleDocumentDownload(documentId, fileName);
            return;
        }

        try {
            setCurrentLoadingDoc(documentId);
            setIsLoading(true);

            const response = await fetch(`${configuration.apiUrl}/api/documents/${documentId}`, {
                headers: {
                    Authorization: `Bearer ${keycloak.token}`
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch document: ${response.status}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            setPreviewUrl(url);
            setSelectedDocument(fileName);
            setIsPreviewOpen(true);
        } catch (error) {
            console.error('Error previewing document:', error);
        } finally {
            setIsLoading(false);
            setCurrentLoadingDoc(null);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        setSelectedDocument(null);
    };

    const getDocumentTypeName = (type) => {
        switch (type?.toUpperCase()) {
            case 'JUSTIFICATION':
                return 'Justification Documents';
            case 'REPORT':
                return 'Report Documents';
            case 'RECEIPT':
                return 'Receipt Documents';
            case 'ATTENDANCE':
                return 'Attendance Documents';
            default:
                return 'Other Documents';
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!selectedRequest) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <Typography>Trip not found</Typography>
            </Box>
        );
    }

    return (
        <MainCard>
            <Stack spacing={3}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h4" sx={{ fontWeight: 600, color: 'white' }}>
                        Trip Request Details - {selectedRequest.sequenceNumber} - {selectedRequest.travelPlan?.planName || 'N/A'}
                    </Typography>
                </Box>

                {/* Basic Information */}
                <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            fontWeight: 600
                        }}
                    >
                        <BusinessIcon sx={{ mr: 2 }} /> Basic Information
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Traveler
                            </Typography>
                            <Chip
                                icon={<PersonIcon />}
                                label={`${selectedRequest.user?.firstName} ${selectedRequest.user?.lastName}`}
                                sx={{
                                    bgcolor: theme.primary.light,
                                    color: theme.primary.main
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Trip Purpose
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedRequest.travelPlan?.planName || 'N/A'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Budget Line
                            </Typography>
                            <Chip
                                icon={<CodeIcon />}
                                label={selectedRequest.budgetLine?.code}
                                sx={{
                                    bgcolor: theme.primary.light,
                                    color: theme.primary.main
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Travel Details */}
                <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            fontWeight: 600
                        }}
                    >
                        <CalendarIcon sx={{ mr: 2 }} /> Travel Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Departure Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatDate(selectedRequest.departureDatetime)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Return Date
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatDate(selectedRequest.returnDatetime)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Budget Amount
                            </Typography>
                            <Typography variant="h5" color={theme.primary.main} sx={{ fontWeight: 600 }}>
                                {formatCurrency(selectedRequest.budgetAmount)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Current Status
                            </Typography>
                            <Chip
                                label={selectedRequest.status?.replace(/_/g, ' ')}
                                sx={{
                                    bgcolor: theme.primary.light,
                                    color: theme.primary.main,
                                    fontWeight: 500
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {/* Documents Section */}
                <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            fontWeight: 600
                        }}
                    >
                        <ArticleIcon sx={{ mr: 2 }} /> Travel Plan Documents
                    </Typography>
                    <Box sx={{ mb: 3 }}>
                        {isLoading && !currentLoadingDoc && (
                            <Typography variant="body2" color="text.secondary">
                                Loading documents...
                            </Typography>
                        )}

                        {documents && documents.length > 0 ? (
                            Object.entries(
                                documents.reduce((acc, doc) => {
                                    const type = doc.documentType || 'OTHER';
                                    if (!acc[type]) acc[type] = [];
                                    acc[type].push(doc);
                                    return acc;
                                }, {})
                            ).map(([type, docs]) => (
                                <Box key={type} sx={{ mb: 4 }}>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            color: theme.primary.main,
                                            fontWeight: 600,
                                            mb: 2
                                        }}
                                    >
                                        {getDocumentTypeName(type)}
                                    </Typography>
                                    {docs.map((doc) => {
                                        const { icon, canPreview } = getFileDetails(doc.fileName);
                                        return (
                                            <Box
                                                key={doc.id}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    mt: 2
                                                }}
                                            >
                                                <Button
                                                    variant="outlined"
                                                    startIcon={icon}
                                                    onClick={() => handleDocumentPreview(doc.id, doc.fileName)}
                                                    disabled={isLoading && currentLoadingDoc === doc.id}
                                                    sx={{
                                                        flexGrow: 1,
                                                        justifyContent: 'flex-start',
                                                        textTransform: 'none'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'flex-start'
                                                        }}
                                                    >
                                                        <Typography variant="body2">{doc.fileName || 'Unnamed Document'}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatFileSize(doc.fileSize)}
                                                        </Typography>
                                                    </Box>
                                                </Button>

                                                {canPreview && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleDocumentPreview(doc.id, doc.fileName)}
                                                        disabled={isLoading && currentLoadingDoc === doc.id}
                                                        size="small"
                                                        title="Preview"
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                )}

                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handleDocumentDownload(doc.id, doc.fileName)}
                                                    disabled={isLoading && currentLoadingDoc === doc.id}
                                                    size="small"
                                                    title="Download"
                                                >
                                                    <FileDownloadIcon />
                                                </IconButton>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                No documents available
                            </Typography>
                        )}
                    </Box>
                </Paper>

                {/* Budget Line Details */}
                <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            fontWeight: 600
                        }}
                    >
                        <DescriptionSharp sx={{ mr: 2 }} /> Budget Line Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Description
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedRequest.budgetLine?.description}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Fiscal Year
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedRequest.budgetLine?.fiscalYear}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Initial Budget
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatCurrency(selectedRequest.budgetLine?.initialBudget)}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Current Balance
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatCurrency(selectedRequest.budgetLine?.currentBalance)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Travel Plan Details */}
                <Paper elevation={0} sx={{ p: 4, bgcolor: 'white', borderRadius: 3 }}>
                    <Typography
                        variant="h6"
                        sx={{
                            color: theme.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            mb: 3,
                            fontWeight: 600
                        }}
                    >
                        <LocationIcon sx={{ mr: 2 }} /> Travel Plan Details
                    </Typography>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Type
                            </Typography>
                            <Chip
                                label={selectedRequest.travelPlan?.type?.replace(/_/g, ' ')}
                                sx={{
                                    bgcolor: theme.primary.light,
                                    color: theme.primary.main
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color={theme.text.secondary} gutterBottom>
                                Venue
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {selectedRequest.travelPlan?.venue || 'N/A'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Daily Activities Section */}
                <TableCollapsible lineActivities={lineActivities} />
            </Stack>

            {/* Document Preview Dialog */}
            <Dialog
                open={isPreviewOpen}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
                sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: theme.primary.main,
                        color: 'white'
                    }}
                >
                    <Typography variant="h6">{selectedDocument || 'Document Preview'}</Typography>
                    <IconButton onClick={handleClosePreview} sx={{ color: 'white' }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: '100%' }}>
                    {previewUrl &&
                        (getFileDetails(selectedDocument).extension === 'pdf' ? (
                            <iframe src={previewUrl} title="PDF Preview" width="100%" height="100%" style={{ border: 'none' }} />
                        ) : (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    bgcolor: 'black'
                                }}
                            >
                                <img
                                    src={previewUrl}
                                    alt={selectedDocument}
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                    }}
                                />
                            </Box>
                        ))}
                </DialogContent>
            </Dialog>
        </MainCard>
    );
};

export default TripDetails;
